import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import "./ContentSwitcher.css";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import bgrandomice from "../../../icebg.png";
import Loader from "./Loader";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const ContentSwitcher = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [productShowcase, setProductShowcase] = useState([]);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("categories");
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

  const sectionRef = useRef(null);
  const backToCategoryRef = useRef(null);
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const topCategoryId = query.get("topCategory");
  const productCategoryId = query.get("productCategory");
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (shouldScrollToTop) {
      const yOffset = -100;
      const element = sectionRef.current;

      if (element) {
        const y =
          element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }

      setShouldScrollToTop(false);
    }
  }, [shouldScrollToTop, view, activeCategory]);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/categories`);
        setCategories(response.data);

        // Match category from URL or default to first
        const matchedCategory = response.data.find(
          (cat) => String(cat.id) === topCategoryId
        );
        const selectedCategory = matchedCategory || response.data[0];

        setActiveCategory(selectedCategory);
        setBackgroundImage(
          `${API_URL}${selectedCategory.image.replace(/\\/g, "/")}`
        );
      } catch (error) {
        console.error("Error fetching categories:", error.message);
        alert("Failed to fetch categories.");
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [topCategoryId]);
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!activeCategory) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/product_categories/category/${activeCategory.id}`
        );
        setSubcategories(response.data);

        // Always try to show product when productCategoryId is set
        const match = response.data.find(
          (subcat) => String(subcat.id) === productCategoryId
        );

        if (match) {
          await handleShopNowClick(activeCategory.id, match.id);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, [activeCategory, productCategoryId]); // ✅ Add productCategoryId here

  const handleSectionChange = (category) => {
    setActiveCategory(category);
    setBackgroundImage(`${API_URL}${category.image.replace(/\\/g, "/")}`);
    setView("categories");
    setProductShowcase([]);
    setShouldScrollToTop(true);

    // ✅ Update the URL to remove productCategoryId
    navigate(`/gallery?topCategory=${category.id}`, { replace: true });

    // ✅ Scroll to top
    setTimeout(() => {
      if (sectionRef.current) {
        window.scrollTo({
          top: sectionRef.current.offsetTop - 100,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleShopNowClick = async (topCategoryId, productCategoryId) => {
    setProductLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/products/category/${productCategoryId}`
      );
      setProductShowcase(response.data);
      setView("products");
      setTimeout(() => {
        scrollToBackButton();
      }, 150);
    } catch (error) {
      console.error("Error fetching product details:", error.message);
      alert("Failed to load product details");
    } finally {
      setProductLoading(false);
    }
  };

  const scrollToBackButton = () => {
    const element = backToCategoryRef.current;
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 50;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div
      ref={sectionRef}
      className={`content-switcher ${loading ? "blur-background" : ""}`}
    >
      {loading ? (
        <Loader />
      ) : (
        <div className="container-fluid">
          <div className="row">
            {/* Sidebar */}
            <div className="col-md-2 p-4 sidebar-custom">
              <h5 className="sidebar-title">Categories</h5>
              <div className="category-list">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={`category-btn ${
                      activeCategory?.id === category.id ? "active" : ""
                    }`}
                    onClick={() => handleSectionChange(category)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="col-md-10 right-section">
              {/* Header Section */}
              <div className="row no-gutters-content">
                <div className="col-md-12 pt-5">
                  <h1 className="title-about">{activeCategory?.name}</h1>
                  {activeCategory?.description && (
                    <p className="category-description">
                      {activeCategory.description}
                    </p>
                  )}
                </div>
              </div>

              {view === "products" && (
                <div className="my-4" ref={backToCategoryRef}>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setView("categories")}
                  >
                    ← Back to Categories
                  </button>
                </div>
              )}

              {/* Dynamic Content */}
              <div className="container my-5">
                {view === "categories" ? (
                  subcategories.length > 0 ? (
                    subcategories.map((product) => (
                      <div key={product.id} className={`row card-container`}>
                        <div className="col-md-9 card-desc">
                          <h5 className="card-title-prod">{product.title}</h5>
                          <p className="card-text">{product.description}</p>
                          <img
                            src={bgrandomice}
                            className="bg-random"
                            alt="Background"
                          />
                          <button
                            className="button-gallery"
                            onClick={() =>
                              handleShopNowClick(activeCategory.id, product.id)
                            }
                          >
                            <span className="Wwp-span">View Products</span>
                          </button>
                        </div>
                        <div className="col-md-3">
                          <div className="product-img-container">
                            <img
                              src={`${API_URL}${product.image}`}
                              className="product-img"
                              alt={product.title}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No products found for this category</p>
                  )
                ) : productShowcase.length > 0 ? (
                  <div className="row">
                    {productShowcase.map((product) => (
                      <div
                        key={product.id}
                        className="col-md-4 col-lg-3 mb-4 d-flex"
                      >
                        <div className="cardprod h-100 text-center">
                          <div className="image-product-div">
                            <img
                              src={`${API_URL}${product.image}`}
                              className="card-img-top"
                              alt={product.name}
                            />
                          </div>
                          <div className="card-body">
                            <h5 className="card-title">{product.name}</h5>
                            <p className="card-text">
                              Quantity: {product.quantity}
                            </p>
                            <button className="btn-primary">
                              <span className="btn-label">Shop Now</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No products available in this section.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentSwitcher;
